
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Trash2, Plus, ShieldAlert, X, Edit, Lock, CheckCircle, XCircle, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const ManageUsers = () => {
    const { currency } = useAuth();
    const [users, setUsers] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'user' });
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/users');
            setUsers(res.data);
        } catch (err) {
            console.error('Error fetching users', err);
        }
    };

    const handleImpersonate = async (id: string) => {
        try {
            const adminToken = localStorage.getItem('token');
            const res = await api.post(`/auth/impersonate/${id}`);
            const userToken = res.data.token;

            if (adminToken && userToken) {
                localStorage.setItem('impersonatorToken', adminToken);
                localStorage.setItem('token', userToken);
                window.location.href = '/';
            }
        } catch (err: any) {
            alert(err.response?.data?.message || 'Error starting preview');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await api.delete(`/users/${id}`);
            setUsers(users.filter(u => u.id !== id));
        } catch (err: any) {
            alert(err.response?.data?.message || 'Error deleting user');
        }
    };

    const handleEdit = (user: any) => {
        setFormData({ name: user.name, email: user.email, password: '', role: user.role });
        setEditingUserId(user.id);
        setShowModal(true);
        setError('');
    };

    const handleCreate = () => {
        setFormData({ name: '', email: '', password: '', role: 'user' });
        setEditingUserId(null);
        setShowModal(true);
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (editingUserId) {
                const res = await api.put(`/users/${editingUserId}`, formData);
                setUsers(users.map(u => u.id === editingUserId ? res.data : u));
            } else {
                const res = await api.post('/users', formData);
                setUsers([...users, res.data]);
            }
            setShowModal(false);
            setFormData({ name: '', email: '', password: '', role: 'user' });
            setEditingUserId(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error saving user');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (id: string, status: string) => {
        try {
            const res = await api.put(`/users/${id}`, { status, isApproved: status === 'ACTIVE' });
            setUsers(users.map(u => u.id === id ? res.data : u));
        } catch (err: any) {
            alert(err.response?.data?.message || 'Error updating user status');
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <ShieldAlert className="text-blue-600" size={28} />
                    <h1 className="text-2xl font-bold text-gray-800">Manage Users</h1>
                </div>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                >
                    <Plus size={20} />
                    Add User
                </button>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="text-left py-4 px-6 font-medium text-gray-600">Name</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-600">Email</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-600">Role</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-600">Status</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-600">Payments unReceived</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-600">Payments Received</th>
                            <th className="text-left py-4 px-6 font-medium text-gray-600">Joined</th>
                            <th className="text-right py-4 px-6 font-medium text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                                <td className="py-4 px-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                                            {user.name.charAt(0)}
                                        </div>
                                        <span className="font-medium text-gray-800">{user.name}</span>
                                    </div>
                                </td>
                                <td className="py-4 px-6 text-gray-600">{user.email}</td>
                                <td className="py-4 px-6">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                                        }`}>
                                        {user.role}
                                    </span>
                                </td>
                                <td className="py-4 px-6">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${(user.status === 'ACTIVE' || (user.isApproved && user.status !== 'SUSPENDED'))
                                        ? 'bg-green-100 text-green-700'
                                        : user.status === 'SUSPENDED'
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                        {(user.status === 'ACTIVE' || (user.isApproved && user.status !== 'SUSPENDED')) ? 'Active' : user.status === 'SUSPENDED' ? 'Suspended' : 'Pending'}
                                    </span>
                                </td>
                                <td className="py-4 px-6">
                                    <span className="font-semibold text-gray-800">{formatCurrency(user.totalPaymentsUnreceived || 0, user.currency || currency)}</span>
                                </td>
                                <td className="py-4 px-6">
                                    <span className="font-semibold text-gray-800">{formatCurrency(user.totalPaymentsReceived || 0, user.currency || currency)}</span>
                                </td>
                                <td className="py-4 px-6 text-gray-500 text-sm">
                                    {new Date(user.createdAt).toLocaleDateString()}
                                </td>
                                <td className="py-4 px-6 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <button
                                            onClick={() => handleImpersonate(user.id)}
                                            className="text-gray-600 hover:text-blue-600 p-1"
                                            title="Preview User Account"
                                        >
                                            <Eye size={18} />
                                        </button>
                                        {(user.status === 'ACTIVE' || (user.isApproved && user.status !== 'SUSPENDED')) ? (
                                            <button
                                                onClick={() => handleStatusChange(user.id, 'SUSPENDED')}
                                                className="text-red-600 hover:text-red-800 p-1"
                                                title="Suspend User"
                                            >
                                                <XCircle size={18} />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleStatusChange(user.id, 'ACTIVE')}
                                                className="text-green-600 hover:text-green-800 p-1"
                                                title="Activate User"
                                            >
                                                <CheckCircle size={18} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleEdit(user)}
                                            className="text-blue-600 hover:text-blue-800 p-1"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(user.id)}
                                            className="text-red-600 hover:text-red-800 p-1"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-800">{editingUserId ? 'Edit User' : 'Create New User'}</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>

                        {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                <input
                                    type="text"
                                    placeholder="John Doe"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    placeholder="john@example.com"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                                    {editingUserId && <Lock size={14} className="text-gray-400" />}
                                    Password {editingUserId && <span className="text-gray-400 font-normal">(Leave blank to keep current)</span>}
                                </label>
                                <input
                                    type="password"
                                    placeholder={editingUserId ? "••••••" : "******"}
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required={!editingUserId}
                                    minLength={6}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={formData.role}
                                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="user">User</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition mt-2 ${loading ? 'opacity-70' : ''}`}
                            >
                                {loading ? 'Saving...' : (editingUserId ? 'Update User' : 'Create User')}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageUsers;
